import { WaitingQueue } from 'owa-promise-queue/lib/WaitingQueue';
import { getGuid } from 'owa-guid';
import {
    SubstrateSearchSuggestionsResponse,
    SubstrateSearchSuggestionsResponsePeopleGroup,
} from 'owa-search-service/lib/data/schema/SubstrateSearchSuggestionsResponse';
import FindRecipientPersonaType from 'owa-recipient-types/lib/types/FindRecipientPersonaType';
import FindPeopleResponseMessage from 'owa-service/lib/contract/FindPeopleResponseMessage';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import { getSuggestionsService } from './getSuggestionsService';
import convertSuggestionToPersonaType from 'owa-recipient-suggestions/lib/util/convertSuggestionToPersonaType';
import { parseLocalPart } from 'owa-recipient-email-address/lib/utils/isValidRecipientAddress';

const RESOLVE_PENDING_REQUEST_COUNT_THRESHOLD = 3;
const searchSuggestionsQueue = new WaitingQueue<
    SubstrateSearchSuggestionsResponse | FindPeopleResponseMessage | null | undefined
>(RESOLVE_PENDING_REQUEST_COUNT_THRESHOLD);

const isSearchSuggestionsResponse = (
    response: SubstrateSearchSuggestionsResponse | FindPeopleResponseMessage
): response is SubstrateSearchSuggestionsResponse => {
    return (response as any).Groups !== undefined;
};

const isSingleGroup = (group): group is SubstrateSearchSuggestionsResponsePeopleGroup =>
    group.Type === 'People' && group.Suggestions.length === 1;

export async function searchSuggestionsForSinglePersona(
    userIdentity: string,
    searchString: string
): Promise<FindRecipientPersonaType | null> {
    const suggestionsService = getSuggestionsService();

    const correlationId = getGuid();
    const response = await searchSuggestionsQueue.queueTask(() =>
        suggestionsService(
            userIdentity,
            searchString,
            true, // searchDirectory
            correlationId,
            null, // feedbackManager
            [] // currentRecipients
        )
    );

    if (!response) {
        return null;
    }

    // If we only got one suggestion and one of the following is true:
    // - The suggestions email is the same as the search string
    // - The suggestion display name is the same as the search string
    // - The suggestion email, local part, is the same as the search string
    // Resolve the persona to the returned and matched suggestion
    if (isSearchSuggestionsResponse(response)) {
        const matchingGroups = response.Groups.filter(isSingleGroup);
        if (matchingGroups.length !== 1) {
            return null;
        }
        const returnedSuggestion = matchingGroups[0].Suggestions[0];
        if (returnedSuggestion.EmailAddresses.length < 1) {
            return null;
        }

        if (
            isQueryExactMatch(
                searchString,
                returnedSuggestion.DisplayName,
                returnedSuggestion.EmailAddresses
            )
        ) {
            const transactionId = response.Instrumentation?.TraceId;
            return convertSuggestionToPersonaType(
                returnedSuggestion,
                transactionId,
                true // returnMaskedRecipients
            );
        }
    } else {
        const resultSet = response.ResultSet;
        if (!resultSet || resultSet.length !== 1) {
            return null;
        }
        const returnedSuggestion = resultSet[0];
        if (!returnedSuggestion || returnedSuggestion?.EmailAddresses?.length == 0) {
            return null;
        }

        const smtps = returnedSuggestion?.EmailAddresses
            ? returnedSuggestion?.EmailAddresses?.map(emailAddressWrapper => {
                  return emailAddressWrapper.EmailAddress;
              })
            : [];
        if (isQueryExactMatch(searchString, returnedSuggestion?.DisplayName, smtps)) {
            return returnedSuggestion as typeof returnedSuggestion & {
                EmailAddress: EmailAddressWrapper;
            };
        }
    }

    return null;
}

function isQueryExactMatch(
    query: string,
    displayName: string | undefined,
    smtps: (string | undefined)[]
): boolean {
    query = query.toLowerCase();
    displayName = displayName?.toLowerCase();

    if (query == displayName) {
        return true;
    }

    for (let i = 0; i < smtps.length; i++) {
        const smtp = smtps[i];
        if (smtp && (query == smtp || query == parseLocalPart(smtp.toLowerCase()))) {
            return true;
        }
    }

    return false;
}
